import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ChevronDown, ChevronUp, ExternalLink, GripVertical, Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { SERVICES } from "@/data/services";

const applyOrder = (list, order) => {
  if (!order.length) return list;
  const byId = Object.fromEntries(list.map((s) => [s.id, s]));
  const ordered = order.map((id) => byId[id]).filter(Boolean);
  const rest = list.filter((s) => !order.includes(s.id));
  return [...ordered, ...rest];
};

export default function OwnerCatalogue() {
  const token = localStorage.getItem("er-owner-token") || "";
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/catalogue/order`)
      .then((res) => setItems(applyOrder(SERVICES, res.data.order || [])))
      .catch(() => setItems(SERVICES));
  }, []);

  const move = (from, to) => {
    setItems((list) => {
      const next = [...list];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/catalogue-order`, { order: items.map((s) => s.id) }, { headers });
      toast.success("Catalogue order saved — the webpage now shows it this way");
    } catch {
      toast.error("Could not save — are you signed in?");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setItems(SERVICES);
    try {
      await axios.put(`${API}/owner/catalogue-order`, { order: [] }, { headers });
      toast.success("Back to the original order");
    } catch {
      toast.error("Could not reset.");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#070709] px-5 text-center">
        <p className="font-serif text-2xl font-light text-neutral-300">Sign in at the owner desk first.</p>
        <Link to="/owner" className="mt-6 border border-amber-500/50 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black">
          Admin Access
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="owner-catalogue" className="min-h-screen bg-[#070709] text-neutral-100">
      <header className="sticky top-0 z-40 border-b border-amber-500/15 bg-[#0c0c10]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="" className="h-10 w-auto mix-blend-screen" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Concierge Desk</p>
              <h1 className="font-serif text-xl font-light text-neutral-50">Webpage Layout</h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="/#services"
              target="_blank"
              rel="noreferrer"
              data-testid="catalogue-view-live"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View live page
            </a>
            <button
              data-testid="catalogue-reset"
              onClick={reset}
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Original order
            </button>
            <button
              data-testid="catalogue-save"
              onClick={save}
              disabled={saving || !items}
              className="flex items-center gap-2 bg-amber-500 px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-300 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Order
            </button>
            <Link
              to="/owner"
              data-testid="catalogue-back"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Requests
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <p className="max-w-2xl text-sm leading-relaxed text-neutral-400">
          This is your webpage's catalogue, card for card. Drag any card — or use the arrows — to rearrange how
          experiences appear on the site, then press <span className="text-amber-200">Save Order</span>. The Iceland
          section comes first, the Worldwide Collection keeps the same relative order at the end.
        </p>

        {!items && (
          <div className="mt-16 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
          </div>
        )}

        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items?.map((s, i) => (
            <motion.div
              key={s.id}
              data-testid={`catalogue-item-${s.id}`}
              draggable
              onDragStart={() => {
                dragRef.current = i;
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragRef.current !== null && dragRef.current !== i) move(dragRef.current, i);
                dragRef.current = null;
              }}
              layout
              className="flex items-center gap-3 border border-amber-500/15 bg-[#101014] p-3 transition-colors hover:border-amber-500/40"
            >
              <div className="flex shrink-0 flex-col">
                <button
                  data-testid={`catalogue-up-${s.id}`}
                  disabled={i === 0}
                  onClick={() => move(i, i - 1)}
                  className="text-neutral-500 transition-colors hover:text-amber-300 disabled:opacity-25"
                  aria-label="Move up"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`catalogue-down-${s.id}`}
                  disabled={i === items.length - 1}
                  onClick={() => move(i, i + 1)}
                  className="text-neutral-500 transition-colors hover:text-amber-300 disabled:opacity-25"
                  aria-label="Move down"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>
              <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-neutral-700" />
              <span className="w-7 shrink-0 text-center font-luxe text-sm tracking-widest text-amber-300/70">{i + 1}</span>
              <img src={s.image} alt="" loading="lazy" className="h-14 w-20 shrink-0 border border-neutral-800 object-cover" />
              <div className="min-w-0">
                <p className="truncate font-serif text-base text-neutral-100">{s.name}</p>
                <p className="text-[9px] uppercase tracking-[0.25em] text-amber-300/60">{s.tag}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
